import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import store from '@/store';
import invitacionesService from '@/services/perfil/invitaciones';
import { toError } from '@/services/perfil/_normalize';
import { calcularEdad } from './utils';

/**
 * useStepperDatos — Estado y operaciones del Paso 3 (datos del comprador e invitados).
 *
 * Responsabilidades:
 *  - Formulario del comprador principal.
 *  - Campos custom del evento (evento.fields).
 *  - Datos nominativos por butaca (evento.solicitar_datos).
 *  - Construcción del payload y guardado en backend.
 *  - Detección de pago requerido (amount > 0, jet_id presente o formulario RedSys presente).
 *  - Guards: hasDatosPorButaca, hasCamposCustom, puedeAvanzarPasoDatos,
 *            pagoRequerido, esFlujoSinPago.
 *
 * @param {import('vue').Ref<Object|null>} evento               Ref del evento activo
 * @param {() => string|number}             getAbonadoId         Función que retorna el ID del abonado
 * @param {import('vue').Ref<Array>}        asientosSeleccionados Ref de asientos elegidos en Paso 2
 * @param {import('vue').Ref<Object|null>}  zonaSeleccionada     Ref de la zona elegida en Paso 1
 * @param {import('vue').Ref<string|null>}  sessionId            Ref del sessionId obtenido al bloquear
 */
export function useStepperDatos(evento, getAbonadoId, asientosSeleccionados, zonaSeleccionada, sessionId) {

    // ─── State: comprador ─────────────────────────────────────────────────
    const formDatosComprador = ref({
        nombre: '',
        apellidos: '',
        tipoDocumento: 'DNI',
        dni: '',
        email: '',
        emailConfirmacion: '',
        fecha_nacimiento: '',
        telefono: '',
        cp: '',
    });

    // ─── State: campos custom ─────────────────────────────────────────────
    /**
     * Campos dinámicos del evento (evento.fields).
     * Estructura: { [field_key]: value }
     */
    const formCamposCustom = ref({});

    // ─── State: datos por butaca ──────────────────────────────────────────
    /**
     * Datos nominativos por butaca cuando evento.solicitar_datos === true.
     * Estructura: Array<{ nombre, apellidos, telefono }>
     */
    const formDatosPorButaca = ref([]);

    // ─── State: guardado ──────────────────────────────────────────────────
    const loadingGuardarDatos = ref(false);
    const errorGuardarDatos = ref(null);

    /**
     * Resultado de guardarDatosComprador.
     * Si amount > 0 o jet_id está presente → pago requerido (fuera de alcance).
     */
    const amountResult = ref(0);
    const jetIdResult  = ref(null);
    const formularioResult = ref(null);

    // ─── Computed: teatro ─────────────────────────────────────────────────

    const teatro = computed(() => store.getters.getTeatro);

    /**
     * Hash del teatro activo. Se usa para decidir si mostrar/requerir
     * el campo teléfono por butaca (oculto para el hash especial).
     */
    const teatroHash = computed(() => teatro.value?.hash ?? null);

    // ─── Computed: guards ─────────────────────────────────────────────────

    /**
     * `true` si el evento activo requiere datos nominativos por butaca.
     */
    const hasDatosPorButaca = computed(
        () => Boolean(evento.value?.solicitar_datos)
    );

    /**
     * `true` si el evento activo tiene campos custom que rellenar.
     */
    const hasCamposCustom = computed(
        () => Array.isArray(evento.value?.fields) && evento.value.fields.length > 0
    );

    /**
     * `true` cuando la respuesta de guardarDatos indica que se necesita pago.
     */
    const pagoRequerido = computed(
        () => amountResult.value > 0 || Boolean(jetIdResult.value) || Boolean(formularioResult.value)
    );

    /**
     * Inverso legible de pagoRequerido.
     */
    const esFlujoSinPago = computed(
        () => !pagoRequerido.value
    );

    /**
     * `true` cuando la respuesta de guardarDatos contiene un formulario RedSys.
     */
    const esPagoRedsys = computed(() => Boolean(formularioResult.value));

    /**
     * `true` cuando la respuesta de guardarDatos contiene un jet_id de Paycomet.
     */
    const esPagoPaycomet = computed(() => Boolean(jetIdResult.value));

    /**
     * Paso 3 → puede avanzar si los campos obligatorios del comprador
     * principal y los datos por butaca (si aplica) están cubiertos.
     *
     * Campos requeridos del comprador:
     *   nombre, apellidos, tipoDocumento, dni, email, emailConfirmacion (coincidentes),
     *   telefono, fecha_nacimiento (no futura), cp.
     */
    const puedeAvanzarPasoDatos = computed(() => {
        const { nombre, apellidos, tipoDocumento, dni, email, emailConfirmacion, telefono, fecha_nacimiento, cp } = formDatosComprador.value;

        // Fecha de nacimiento: requerida y no puede ser posterior a hoy
        const hoy = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const fechaValida = fecha_nacimiento?.trim() && fecha_nacimiento.trim() <= hoy;

        const compradorValido = Boolean(
            nombre?.trim() &&
            apellidos?.trim() &&
            tipoDocumento?.trim() &&
            dni?.trim() &&
            email?.trim() &&
            emailConfirmacion?.trim() &&
            email.trim().toLowerCase() === emailConfirmacion.trim().toLowerCase() &&
            telefono?.trim() &&
            fechaValida &&
            cp?.trim()
        );
        if (!compradorValido) return false;

        // El teléfono por butaca no se requiere si el teatro tiene el hash especial
        // (misma condición que el legacy: v-if="teatro.hash != '14wAm2L1Jxx5B3J3xBkH'")
        const telefonoRequerido = teatroHash.value !== '14wAm2L1Jxx5B3J3xBkH';

        if (formDatosPorButaca.value.length > 0) {
            return formDatosPorButaca.value.every(b =>
                b.nombre?.trim() && b.apellidos?.trim() && (telefonoRequerido ? b.telefono?.trim() : true)
            );
        }

        return true;
    });

    // ─── Inicialización de campos ─────────────────────────────────────────

    /**
     * Crea los slots de datos por butaca según la cantidad de asientos seleccionados.
     * Solo tiene efecto si el evento solicita datos nominativos.
     */
    const inicializarDatosPorButaca = () => {
        if (!evento.value?.solicitar_datos) {
            formDatosPorButaca.value = [];
            return;
        }
        const count = asientosSeleccionados.value.length;
        formDatosPorButaca.value = Array.from({ length: count }, () => ({
            nombre: '',
            apellidos: '',
            telefono: '',
        }));
    };

    /**
     * Inicializa formCamposCustom con claves vacías para cada field del evento.
     * Preserva valores ya introducidos si el usuario volvió al paso.
     */
    const inicializarCamposCustom = () => {
        if (!evento.value?.fields?.length) {
            formCamposCustom.value = {};
            return;
        }
        const existing = formCamposCustom.value;
        const fresh = {};
        for (const field of evento.value.fields) {
            fresh[field.key] = existing[field.key] ?? '';
        }
        formCamposCustom.value = fresh;
    };

    // ─── Guardado ─────────────────────────────────────────────────────────

    /**
     * Construye el payload para guardarDatosComprador a partir de los forms.
     *
     * @returns {Object}
     */
    const _buildDatosPayload = () => {
        const { nombre, apellidos, dni, email, telefono, fecha_nacimiento } = formDatosComprador.value;

        const zonaId = zonaSeleccionada.value?.id ?? null;

        const asientos = asientosSeleccionados.value.map((a, i) => {
            const butaca = formDatosPorButaca.value[i] ?? {};
            return {
                asiento: {
                    fila:    a.row    ?? a.fila    ?? '',
                    asiento: a.number ?? a.asiento ?? a.numero ?? '',
                    zona_id: zonaId,
                },
                nombre:    butaca.nombre    ?? '',
                apellidos: butaca.apellidos ?? '',
                dni:       butaca.dni       ?? '',
                telefono:  butaca.telefono  ?? '',
            };
        });

        return {
            filters:    {},
            abonado_id: String(getAbonadoId()),
            evento_id:  String(evento.value?.evento_id ?? ''),
            sesion_id:  String(evento.value?.sesion_id ?? ''),
            session_id: String(sessionId.value ?? ''),
            datos_compradores: {
                email,
                nombre,
                apellidos,
                telefono,
                dni,
                edad: calcularEdad(fecha_nacimiento),
                asientos,
            },
        };
    };

    /**
     * Guarda los datos del comprador en el backend.
     * NO avanza de paso — el orchestrator decide el siguiente paso.
     * Siempre retorna `true` en éxito; el orchestrator consulta `pagoRequerido` para decidir
     * si permite finalizar la compra o muestra UI de pago pendiente.
     *
     * @returns {Promise<boolean>} `true` en éxito, `false` en error
     */
    const guardarDatos = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId || !evento.value || !sessionId.value) return false;

        loadingGuardarDatos.value = true;
        errorGuardarDatos.value = null;

        try {
            const payload = _buildDatosPayload();
            const result = await invitacionesService.guardarDatosComprador(
                abonadoId,
                evento.value.evento_id,
                evento.value.sesion_id,
                payload,
            );
            amountResult.value = result.amount ?? 0;
            jetIdResult.value  = result.jet_id ?? null;
            formularioResult.value = result.formulario ?? null;

            return true;
        } catch (err) {
            const normalized = toError(err);
            errorGuardarDatos.value = normalized.message ??
                t('perfil.invitaciones.stepper.error_guardar_datos', {}, 'Error al guardar los datos. Por favor, inténtalo de nuevo.');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useStepperDatos] Error al guardar datos:', err);
            }
            return false;
        } finally {
            loadingGuardarDatos.value = false;
        }
    };

    // ─── Reset (sin borrar datos del comprador ya rellenados) ─────────────
    /**
     * Resetea solo el estado de sesión/datos del Paso 3, preservando
     * formDatosComprador y formCamposCustom ya que el usuario puede
     * haber rellenado algo al retroceder y avanzar de nuevo.
     */
    const resetParcial = () => {
        amountResult.value = 0;
        jetIdResult.value = null;
        formularioResult.value = null;
        errorGuardarDatos.value = null;
        formDatosPorButaca.value = [];
    };

    /**
     * Reset completo del Paso 3.
     */
    const reset = () => {
        formDatosComprador.value = {
            nombre: '', apellidos: '', tipoDocumento: 'DNI', dni: '',
            email: '', emailConfirmacion: '', fecha_nacimiento: '', telefono: '', cp: '',
        };
        formCamposCustom.value = {};
        formDatosPorButaca.value = [];
        errorGuardarDatos.value = null;
        amountResult.value = 0;
        jetIdResult.value = null;
        formularioResult.value = null;
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        formDatosComprador,
        formCamposCustom,
        formDatosPorButaca,
        loadingGuardarDatos,
        errorGuardarDatos,
        amountResult,
        jetIdResult,
        formularioResult,
        teatroHash,
        pagoRequerido,
        esFlujoSinPago,
        esPagoRedsys,
        esPagoPaycomet,
        hasDatosPorButaca,
        hasCamposCustom,
        puedeAvanzarPasoDatos,
        inicializarDatosPorButaca,
        inicializarCamposCustom,
        guardarDatos,
        resetParcial,
        reset,
    };
}
